import { CRITTERS } from '../data/balance';
import { addBuff } from './buffs';
import { earn, payoutBasis } from './economy';
import { globalMul } from './modifiers';
import { rand, randInt, randRange } from './rng';
import { emit, findFlock, nextId } from './state';
import type { Critter, CritterKind, GameState, SimCtx } from './types';
import type { ActionResult } from './actions';

/**
 * Ambient clickables that wander the farm. The core owns spawning, rewards
 * and lifetimes; render/ animates them deterministically from spawn data.
 */

type Spawnable = 'chick' | 'feather' | 'worm' | 'butterfly' | 'bird';
const SPAWNABLE: Spawnable[] = ['chick', 'feather', 'worm', 'butterfly', 'bird'];

function eligible(s: GameState, kind: Spawnable): boolean {
  const life = s.lifetimeEarned;
  if (kind === 'worm') return life < CRITTERS.worm.maxLifetime;
  if (kind === 'butterfly') return life < CRITTERS.butterfly.maxLifetime;
  if (kind === 'bird') return life >= CRITTERS.bird.minLifetime;
  return true;
}

function nextDelayMs(s: GameState, ctx: SimCtx, kind: Spawnable): number {
  const p = CRITTERS[kind];
  return (randRange(s, p.everyMinSec, p.everyMaxSec) * 1000) / globalMul(s, ctx, 'critterSpawn');
}

function makeCritter(s: GameState, kind: CritterKind, flockId: number): Critter {
  const params = kind === 'megaWorm' ? CRITTERS.megaWorm : CRITTERS[kind];
  const reward = 'minSec' in params ? { rewardSec: randRange(s, params.minSec, params.maxSec), floor: params.floor } : { rewardSec: 0, floor: 0 };
  const flock = findFlock(s, flockId);
  const hens = flock?.boxes.filter((b) => b.hen).map((b) => b.hen!.breed) ?? [];
  const c: Critter = {
    id: nextId(s),
    kind,
    flockId,
    x: randRange(s, 0.05, 0.95),
    y: randRange(s, 0.1, 0.92),
    heading: rand(s) * Math.PI * 2,
    spawnedAt: s.time,
    expiresAt: s.time + params.lifeSec * 1000,
    rewardSec: reward.rewardSec,
    floor: reward.floor,
    hp: kind === 'megaWorm' ? CRITTERS.megaWorm.hp : 1,
  };
  if (kind === 'feather' && hens.length) c.breed = hens[randInt(s, 0, hens.length - 1)]!;
  return c;
}

export function spawnStormEggs(s: GameState, flockId: number, n: number): void {
  for (let i = 0; i < n; i++) s.critters.push(makeCritter(s, 'stormEgg', flockId));
}

function stormActive(s: GameState): boolean {
  return s.buffs.click.some((b) => b.until > s.time && b.source === 'golden:clickStorm');
}

export function tickCritters(s: GameState, ctx: SimCtx): void {
  const now = s.time;
  for (let i = s.critters.length - 1; i >= 0; i--) if (s.critters[i]!.expiresAt <= now) s.critters.splice(i, 1);
  const flockId = findFlock(s, s.viewFlock)?.id ?? s.flocks[0]!.id;
  const ambient = s.critters.filter((c) => c.kind !== 'stormEgg').length;
  for (const kind of SPAWNABLE) {
    const next = s.timers.critterNext[kind];
    if (next === undefined) {
      s.timers.critterNext[kind] = now + nextDelayMs(s, ctx, kind);
      continue;
    }
    if (now < next) continue;
    s.timers.critterNext[kind] = now + nextDelayMs(s, ctx, kind);
    if (!eligible(s, kind) || ambient >= CRITTERS.maxOnScreen) continue;
    const actual: CritterKind = kind === 'worm' && rand(s) < CRITTERS.worm.megaChance ? 'megaWorm' : kind;
    s.critters.push(makeCritter(s, actual, flockId));
  }
  if (stormActive(s) && now >= s.timers.stormWaveAt) {
    spawnStormEggs(s, flockId, randInt(s, CRITTERS.stormEgg.waveMin, CRITTERS.stormEgg.waveMax));
    s.timers.stormWaveAt = now + CRITTERS.stormEgg.waveEverySec * 1000;
  }
}

export function critterReward(s: GameState, ctx: SimCtx, c: Critter): number {
  if (c.kind === 'butterfly') return 0;
  return Math.max(c.floor, payoutBasis(s, ctx) * c.rewardSec);
}

export function clickCritter(s: GameState, ctx: SimCtx, id: number): ActionResult {
  const c = s.critters.find((x) => x.id === id);
  if (!c) return { ok: false, reason: 'Gone' };
  if (c.kind === 'megaWorm' && c.hp > 1) {
    c.hp--;
    return { ok: true, amount: 0, detail: `${c.hp} more!` };
  }
  s.critters.splice(s.critters.indexOf(c), 1);
  if (c.kind === 'butterfly') {
    addBuff(s.buffs.lay, CRITTERS.butterfly.layMult, CRITTERS.butterfly.durationSec * 1000, s.time, 'butterfly');
    return { ok: true, amount: 0, detail: `Butterfly! 1.5× laying for ${CRITTERS.butterfly.durationSec}s` };
  }
  const amount = earn(s, critterReward(s, ctx, c));
  emit(s, { kind: 'coins', amount, flockId: c.flockId, x: c.x, y: c.y });
  return { ok: true, amount };
}
